/**
 * Output Footer Sections — the content contract shared by the results pages,
 * the print stylesheet, the PDF generator and the admin designer.
 *
 * A section is admin-authored SEMANTIC HTML (headings, paragraphs, lists,
 * links, figures with images and captions, tables, emphasis — no inline styles
 * or classes; the server whitelist guarantees that) plus display modes and
 * page controls. Sections arrive from products.php already resolved for the
 * retailer (universal defaults, replaced / hidden per retailer), ordered, and
 * translated; consumers only filter on `display`.
 *
 * Mirrors public/api/output-sections-helper.php.
 */

export type SectionScreenMode = 'hidden' | 'always' | 'collapsibleOpen' | 'collapsibleClosed';

export interface SectionDisplay {
  /** Draw in the generated PDF. */
  pdf: boolean;
  /** Show under @media print (the printed results page). */
  print: boolean;
  /** On-screen behaviour on the results page. */
  screen: SectionScreenMode;
}

/** Page controls honoured by the PDF generator and the print stylesheet. */
export interface SectionPageControls {
  /** Start the section on a fresh page (never yields a blank page). */
  startOnNewPage: boolean;
  /**
   * Fit the section into at most N pages by scaling ALL of its content down
   * uniformly (never by cutting any of it). null = no limit; 1..SECTION_MAX_PAGES.
   */
  maxPages: number | null;
  /** Nothing that follows may start on the section's last page. */
  breakAfter: boolean;
}

export interface OutputSectionContent {
  html: string;
  display: SectionDisplay;
  page: SectionPageControls;
}

export interface OutputSection {
  id: number;
  key: string;
  title: string;
  content: OutputSectionContent;
  isActive: boolean;
  sortOrder: number;
}

export const SCREEN_MODES: { value: SectionScreenMode; label: string }[] = [
  { value: 'always', label: 'Always visible' },
  { value: 'collapsibleOpen', label: 'Collapsible — starts open' },
  { value: 'collapsibleClosed', label: 'Collapsible — starts closed' },
  { value: 'hidden', label: 'Hidden on screen' },
];

export const DEFAULT_DISPLAY: SectionDisplay = { pdf: true, print: true, screen: 'collapsibleClosed' };
export const DEFAULT_PAGE: SectionPageControls = { startOnNewPage: false, maxPages: null, breakAfter: false };
export const SECTION_MAX_PAGES = 20;
export const SECTION_HTML_MAX = 60000;

const SCREEN_SET = new Set<string>(SCREEN_MODES.map((m) => m.value));

/** Defensive parse of one payload entry; null when it is not a usable section. */
export function normalizeOutputSection(raw: unknown): OutputSection | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  if (!Number.isFinite(id) || id <= 0) return null;
  const c = (r.content && typeof r.content === 'object' ? r.content : {}) as Record<string, unknown>;
  const d = (c.display && typeof c.display === 'object' ? c.display : {}) as Record<string, unknown>;
  const p = (c.page && typeof c.page === 'object' ? c.page : {}) as Record<string, unknown>;
  const maxRaw = Number(p.maxPages);
  return {
    id,
    key: typeof r.key === 'string' ? r.key : String(id),
    title: typeof r.title === 'string' ? r.title : '',
    content: {
      html: typeof c.html === 'string' ? c.html : '',
      display: {
        pdf: d.pdf !== false,
        print: d.print !== false,
        screen: typeof d.screen === 'string' && SCREEN_SET.has(d.screen) ? (d.screen as SectionScreenMode) : DEFAULT_DISPLAY.screen,
      },
      page: {
        startOnNewPage: p.startOnNewPage === true,
        maxPages: Number.isFinite(maxRaw) && maxRaw > 0 ? Math.min(SECTION_MAX_PAGES, Math.floor(maxRaw)) : null,
        breakAfter: p.breakAfter === true,
      },
    },
    isActive: r.isActive !== false,
    sortOrder: Number.isFinite(Number(r.sortOrder)) ? Number(r.sortOrder) : 0,
  };
}

export function normalizeOutputSections(raw: unknown): OutputSection[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeOutputSection).filter((s): s is OutputSection => s !== null);
}
